import type { EmailType } from '@/emails/types'

interface EmailResult {
  success: boolean
  id?: string
  error?: string
}

/**
 * Sends an email via the centralized /api/email endpoint.
 * Requires Auth0 Bearer token for authentication and the organization the email is sent from.
 */
async function enviarViaEndpoint(
  token: string,
  type: EmailType,
  to: string | string[],
  data: Record<string, unknown>,
  options: {
    organizacionId: string
    clienteId?: string
    attachments?: Array<{ filename: string; content: string }>
    documentoIds?: string[]
  }
): Promise<EmailResult> {
  try {
    const response = await fetch('/.netlify/functions/email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        type,
        to,
        data,
        organizacionId: options.organizacionId,
        clienteId: options.clienteId,
        attachments: options.attachments,
        documentoIds: options.documentoIds,
      }),
    })

    const result = await response.json()

    if (!response.ok) {
      console.error('Error enviando email:', result)
      return { success: false, error: result.error || 'Error enviando email' }
    }

    return { success: true, id: result.id }
  } catch (err) {
    console.error('Email service no disponible:', err)
    return { success: false, error: 'Servicio de email no disponible' }
  }
}

export const emailService = {
  /**
   * Envía estado de cuenta a un cliente
   */
  async enviarEstadoCuenta(
    token: string,
    cliente: { id: string; nombre: string; email: string },
    movimientos: Array<{ fecha: string; concepto: string; debe: number; haber: number }>,
    saldo: number,
    organizacionId: string,
    whatsappEstudio?: string
  ): Promise<EmailResult> {
    if (!cliente.email) {
      return { success: false, error: 'El cliente no tiene email configurado' }
    }

    return enviarViaEndpoint(token, 'estado_cuenta', cliente.email, {
      clienteNombre: cliente.nombre,
      movimientos,
      saldo,
      whatsappEstudio,
    }, { organizacionId, clienteId: cliente.id })
  },

  /**
   * Envía recordatorio de deuda a un cliente
   */
  async enviarRecordatorioDeuda(
    token: string,
    cliente: { id?: string; nombre: string; email: string },
    saldo: number,
    diasSinPago: number | undefined,
    organizacionId: string,
    whatsappEstudio?: string
  ): Promise<EmailResult> {
    if (!cliente.email) {
      return { success: false, error: 'El cliente no tiene email configurado' }
    }

    return enviarViaEndpoint(token, 'recordatorio_deuda', cliente.email, {
      clienteNombre: cliente.nombre,
      saldo,
      diasSinPago,
      whatsappEstudio,
    }, { organizacionId, clienteId: cliente.id })
  },

  /**
   * Envía notificación de nuevo cliente creado
   */
  async enviarNotificacionNuevoCliente(
    token: string,
    destinatario: string,
    cliente: { nombre: string; cuit?: number | null; honorarios_mensuales?: number },
    organizacionId: string
  ): Promise<EmailResult> {
    const cuitStr = cliente.cuit
      ? `${cliente.cuit.toString().slice(0, 2)}-${cliente.cuit.toString().slice(2, 10)}-${cliente.cuit.toString().slice(10)}`
      : null

    return enviarViaEndpoint(token, 'nuevo_cliente', destinatario, {
      nombre: cliente.nombre,
      cuit: cuitStr,
      honorariosMensuales: cliente.honorarios_mensuales,
    }, { organizacionId })
  },

  /**
   * Envía notificación de nuevo movimiento
   */
  async enviarNotificacionNuevoMovimiento(
    token: string,
    destinatario: string,
    movimiento: {
      clienteNombre: string
      concepto: string
      monto: number
      tipo: 'debe' | 'haber'
    },
    organizacionId: string
  ): Promise<EmailResult> {
    return enviarViaEndpoint(token, 'nuevo_movimiento', destinatario, movimiento, { organizacionId })
  },

  /**
   * Envía resumen de movimientos generados
   */
  async enviarResumenMovimientos(
    token: string,
    destinatarios: string[],
    stats: {
      clientesCreados: number
      montoTotal: number
      clientesDuplicados: number
      clientesOmitidos: number
    },
    mesAnio: string,
    simulacion: boolean,
    organizacionId: string
  ): Promise<EmailResult> {
    return enviarViaEndpoint(token, 'resumen_movimientos', destinatarios, {
      stats,
      mesAnio,
      simulacion,
    }, { organizacionId })
  },

  /**
   * Envía el recibo de un cobro al cliente, con el PDF adjunto (ORR-336).
   *
   * El detalle va en el PDF y no en el cuerpo: si estuviera en los dos, podrían contradecirse.
   */
  async enviarReciboPago(
    token: string,
    destinatario: string,
    datos: {
      clienteNombre: string
      monto: number
      fecha: string
      lineas: Array<{ periodo: string | null; concepto: string; importe: number }>
      reciboBase64: string
      whatsappEstudio?: string | null
      nombreEstudio?: string | null
    },
    organizacionId: string,
    clienteId?: string,
  ): Promise<EmailResult> {
    const nombreArchivo = `Recibo_${datos.clienteNombre.replace(/[^a-zA-Z0-9]/g, '_')}_${datos.fecha}.pdf`

    return enviarViaEndpoint(token, 'recibo_pago', destinatario, {
      clienteNombre: datos.clienteNombre,
      monto: datos.monto,
      fecha: datos.fecha,
      lineas: datos.lineas,
      whatsappEstudio: datos.whatsappEstudio,
      nombreEstudio: datos.nombreEstudio,
    }, {
      organizacionId,
      clienteId,
      attachments: [{ filename: nombreArchivo, content: datos.reciboBase64 }],
    })
  },

  /**
   * Envía notificación al cliente después de presentar un impuesto
   */
  async enviarPresentadoCliente(
    token: string,
    destinatario: string,
    datos: {
      clienteNombre: string
      tipoImpuesto: string
      periodo: string
      fechaPresentacion: string
      numeroPresentacion?: string | null
      montoDeterminado?: number | null
      saldoTipo?: 'a_pagar' | 'a_favor' | 'sin_movimiento' | null
      adjuntoNombres?: string[]
      mensajePersonalizado: string
      customSubject?: string
      whatsappEstudio?: string | null
      nombreEstudio?: string | null
    },
    options: {
      organizacionId: string
      clienteId?: string
      documentoIds?: string[]
    }
  ): Promise<EmailResult> {
    if (!destinatario) {
      return { success: false, error: 'El cliente no tiene email configurado' }
    }

    return enviarViaEndpoint(token, 'presentado_cliente', destinatario, {
      clienteNombre: datos.clienteNombre,
      tipoImpuesto: datos.tipoImpuesto,
      periodo: datos.periodo,
      fechaPresentacion: datos.fechaPresentacion,
      numeroPresentacion: datos.numeroPresentacion,
      montoDeterminado: datos.montoDeterminado,
      saldoTipo: datos.saldoTipo,
      adjuntoNombres: datos.adjuntoNombres,
      mensajePersonalizado: datos.mensajePersonalizado,
      customSubject: datos.customSubject,
      whatsappEstudio: datos.whatsappEstudio,
      nombreEstudio: datos.nombreEstudio,
    }, {
      organizacionId: options.organizacionId,
      clienteId: options.clienteId,
      documentoIds: options.documentoIds,
    })
  },

  /**
   * Envía presupuesto al cliente con PDF adjunto
   */
  async enviarPresupuesto(
    token: string,
    destinatario: string,
    datos: {
      clienteNombre: string
      numeroPresupuesto: number
      total: number
      fechaValidez?: string | null
      mensajePersonalizado?: string
      customSubject?: string
      whatsappEstudio?: string | null
    },
    options: {
      organizacionId: string
      clienteId?: string
      attachments?: Array<{ filename: string; content: string }>
    }
  ): Promise<EmailResult> {
    if (!destinatario) {
      return { success: false, error: 'El cliente no tiene email configurado' }
    }

    return enviarViaEndpoint(token, 'presupuesto_enviado', destinatario, {
      clienteNombre: datos.clienteNombre,
      numeroPresupuesto: datos.numeroPresupuesto,
      total: datos.total,
      fechaValidez: datos.fechaValidez,
      mensajePersonalizado: datos.mensajePersonalizado,
      customSubject: datos.customSubject,
      whatsappEstudio: datos.whatsappEstudio,
    }, {
      organizacionId: options.organizacionId,
      clienteId: options.clienteId,
      attachments: options.attachments,
    })
  },
}
